import { z } from 'zod';

/**
 * Backstage — platform yönetim paneli sözleşmesi.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Bu dosyadaki hiçbir tip TOTP SIRRINI ya da OTURUM JETONUNU taşımaz
 * ---------------------------------------------------------------------------
 * İki istisna var ve ikisi de tek yönlü:
 *   • `BackstageTotpEnrollment` — kurulum anında sır BİR KEZ dönüyor, ondan
 *     sonra hiçbir uç onu okuyamıyor.
 *   • `BackstageRecoveryCodes` — kodlar üretildiği anda BİR KEZ dönüyor,
 *     DB'de yalnızca argon2 hash'i duruyor.
 *
 * Oturum jetonu bu katmana hiç girmiyor: API onu yanıt BAŞLIĞINDA gönderiyor
 * ve web tarafındaki route handler çereze çeviriyor. Gövdeye koysaydık bir
 * gün bir log satırında ya da bir hata ekranında görünürdü.
 */

// ─────────────────────────────── Sabitler ───────────────────────────────

export const BACKSTAGE_ROLES = ['OWNER', 'OPERATOR', 'AUDITOR'] as const;
export type BackstageRole = (typeof BACKSTAGE_ROLES)[number];

export const BACKSTAGE_ACCESS_STATUSES = ['PENDING_TOTP', 'ACTIVE', 'SUSPENDED'] as const;
export type BackstageAccessStatus = (typeof BACKSTAGE_ACCESS_STATUSES)[number];

/**
 * 🔴 Prisma'daki `BackstageEventType` ile BİREBİR aynı olmalı.
 *
 * Elle kopya bir liste — ve bu bilinçli bir borç: `@mentorivo/shared` frontend
 * derlemesine de giriyor, Prisma client'ı oraya sokamayız. Kopya olduğu için
 * `backstage-audit.service.ts`'te bir eşleşme testi var; şemaya eklenip buraya
 * eklenmeyen bir değer testi kırar.
 */
export const BACKSTAGE_EVENT_TYPES = [
  /// 🔴 TARİHÎ — parola 10 Eki 2026'da kalktı, bu iki tür artık YAZILMIYOR.
  /// Enum'da duruyorlar çünkü eski olay satırları onları taşıyor; silmek o
  /// satırları ya yok etmek ya da başka bir şeymiş gibi yeniden etiketlemek
  /// demekti.
  'LOGIN_PASSWORD_OK',
  'LOGIN_PASSWORD_FAIL',
  /// Birinci adım (10 Eki 2026): e-postaya kod gönderildi / doğru / yanlış.
  'LOGIN_CODE_SENT',
  'LOGIN_CODE_OK',
  'LOGIN_CODE_FAIL',
  'LOGIN_TOTP_OK',
  'LOGIN_TOTP_FAIL',
  'LOGIN_RECOVERY_USED',
  'LOGIN_IP_DENIED',
  'LOGIN_LOCKED',
  'LOGIN_NO_ACCESS',
  'LOGOUT',
  'SESSION_REVOKED',
  'SESSION_IP_CHANGED',
  'TOTP_ENROLL_STARTED',
  'TOTP_ENROLLED',
  'TOTP_RESET',
  'RECOVERY_REGENERATED',
  'ACCESS_GRANTED',
  'ACCESS_ROLE_CHANGED',
  'ACCESS_SUSPENDED',
  'ACCESS_RESTORED',
  'ACCESS_REVOKED',
  'IP_RULE_CREATED',
  'IP_RULE_UPDATED',
  'IP_RULE_DELETED',
  'IP_ENFORCEMENT_CHANGED',
  'NGINX_SYNC_OK',
  'NGINX_SYNC_FAILED',
] as const;
export type BackstageEventType = (typeof BACKSTAGE_EVENT_TYPES)[number];

/**
 * Panelin ekrana bastığı hata kodları.
 *
 * ⚠️ Giriş akışında yalnızca İKİ kod dönüyor: `INVALID_CREDENTIALS` ve
 * `LOCKED`. "Kullanıcı yok", "kod yanlış", "2FA kurulmamış", "erişim
 * askıda" ayrımı ekrana ÇIKMIYOR — hepsi `INVALID_CREDENTIALS`. Ayrım
 * yapılsaydı bir saldırgan hangi e-postanın panel erişimi olduğunu deneme
 * yanılmayla bulabilirdi. Gerçek sebep `BackstageEvent` akışında duruyor.
 */
export const BackstageErrorCode = {
  /** E-posta kodu, TOTP ya da kurtarma kodu tutmadı — hangisi olduğu söylenmiyor. */
  INVALID_CREDENTIALS: 'BS_INVALID_CREDENTIALS',
  /** Çok fazla başarısız deneme; `retryAfterSeconds` dolu geliyor. */
  LOCKED: 'BS_LOCKED',
  /** İkinci adım jetonu süresi doldu — baştan giriş gerekiyor. */
  CHALLENGE_EXPIRED: 'BS_CHALLENGE_EXPIRED',
  /** TOTP kurulumu yarım — kurulum akışına yönlendiriliyor. */
  TOTP_SETUP_REQUIRED: 'BS_TOTP_SETUP_REQUIRED',
  /** Oturum yok ya da süresi doldu. */
  SESSION_EXPIRED: 'BS_SESSION_EXPIRED',
  /** Bu rol bu işlemi yapamaz. */
  FORBIDDEN_ROLE: 'BS_FORBIDDEN_ROLE',
  /** CIDR biçimi geçersiz. */
  INVALID_CIDR: 'BS_INVALID_CIDR',
  /** Kural, çağıranın kendi IP'sini dışarıda bırakıyor. */
  WOULD_LOCK_OUT: 'BS_WOULD_LOCK_OUT',
  /** nginx yazımı/testi başarısız — kural DB'de kaldı, ağ katmanı eski. */
  NGINX_SYNC_FAILED: 'BS_NGINX_SYNC_FAILED',

  // ── Panel yöneticileri (29 Eyl 2026) ──────────────────────────────────
  // Eskiden kodsuz Türkçe cümleydiler ve ekran `message`ı olduğu gibi
  // basıyordu: İngilizce panelde Türkçe hata. Ekran artık koda göre çeviriyor.
  /** Bu e-postayla kayıtlı kullanıcı yok. */
  USER_NOT_FOUND: 'BS_USER_NOT_FOUND',
  /** Kullanıcının panel erişimi zaten var. */
  ALREADY_GRANTED: 'BS_ALREADY_GRANTED',
  /** Kullanıcı platform yöneticisi değil — erişimden önce `/admin`den yapılmalı. */
  NOT_PLATFORM_ADMIN: 'BS_NOT_PLATFORM_ADMIN',
  /** Yönetici kendi rolünü ya da durumunu bu ekrandan değiştiremez. */
  SELF_CHANGE_FORBIDDEN: 'BS_SELF_CHANGE_FORBIDDEN',
  /** Yönetici kendi erişimini kaldıramaz. */
  SELF_REVOKE_FORBIDDEN: 'BS_SELF_REVOKE_FORBIDDEN',
  /** Son etkin OWNER düşürülemez, askıya alınamaz, kaldırılamaz. */
  LAST_OWNER: 'BS_LAST_OWNER',
} as const;
export type BackstageErrorCode = (typeof BackstageErrorCode)[keyof typeof BackstageErrorCode];

// ──────────────────────────────── Giriş ────────────────────────────────

/**
 * Birinci adım — e-postaya altı haneli kod (10 Eki 2026; öncesinde parolaydı).
 *
 * İkinci adım DEĞİŞMEDİ ve değişmeyecek: TOTP ya da kurtarma kodu, tamamen
 * çevrimdışı. E-posta kodu bilinçli olarak BİRİNCİ faktör — ikinci faktör
 * olsaydı panelin iki kilidi de aynı posta kutusuna inerdi
 * (`backstage-auth.service.ts` başı).
 *
 * Kod yalnız panel erişimi GERÇEKTEN olan adrese gidiyor ama yanıt her adrese
 * aynı: "kim platform yöneticisi" sorusu bu uçtan cevaplanamıyor.
 */
export const backstageCodeRequestSchema = z.object({
  email: z.email().max(200),
});
export type BackstageCodeRequestInput = z.infer<typeof backstageCodeRequestSchema>;

export interface BackstageCodeResponse {
  /// Kod kaç saniye geçerli.
  expiresInSeconds: number;
  /// "Kodu tekrar gönder" kaç saniye sonra açılıyor.
  resendInSeconds: number;
}

export const backstageLoginSchema = z.object({
  email: z.email().max(200),
  code: z
    .string()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .pipe(z.string().regex(/^\d{6}$/)),
});
export type BackstageLoginInput = z.infer<typeof backstageLoginSchema>;

/**
 * Birinci adımın çıktısı — oturum DEĞİL.
 *
 * `challengeId` yalnızca ikinci adımı açıyor ve Redis'te kısa ömürlü. E-posta
 * kodu doğru olsa bile bu noktada hiçbir veriye erişim yok.
 */
export interface BackstageChallenge {
  challengeId: string;
  /** Kaç saniye içinde ikinci adım tamamlanmalı. */
  expiresInSeconds: number;
  /** true → TOTP hiç kurulmamış, ikinci adım yerine kurulum ekranı açılır. */
  setupRequired: boolean;
}

export const backstageVerifySchema = z.object({
  challengeId: z.string().min(10).max(200),
  /**
   * 6 haneli TOTP kodu ya da kurtarma kodu. İkisi de aynı alandan geliyor:
   * ayrı alan olsaydı ekran "hangisini giriyorum" sorusunu kullanıcıya
   * sorardı, oysa uzunluğundan belli.
   */
  code: z
    .string()
    .min(6)
    .max(40)
    .transform((v) => v.replace(/[\s-]/g, '')),
});
export type BackstageVerifyInput = z.infer<typeof backstageVerifySchema>;

// ─────────────────────────────── Oturum ───────────────────────────────

export interface BackstageSessionUser {
  userId: string;
  email: string;
  name: string | null;
  role: BackstageRole;
  status: BackstageAccessStatus;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
}

export interface BackstageSessionResponse {
  user: BackstageSessionUser;
  /** Oturumun kendi kimliği — "bu cihazı kapat" listesinde işaretleniyor. */
  sessionId: string;
  expiresAt: string;
  /**
   * Kullanılmamış kurtarma kodu sayısı.
   *
   * Uyarı şeridi zaten "azaldı" diyor ama ekran kesin sayıyı da gösteriyor:
   * "yenilemeli miyim" kararını 3 ile 9 arasındaki fark veriyor ve o farkı
   * bir eşik bayrağından TÜRETMEK mümkün değil.
   */
  recoveryCodesLeft: number;
  /** Panelin üstünde uyarı şeridi basmak için. */
  warnings: BackstageWarning[];
}

/**
 * Panelin kendi sağlık uyarıları.
 *
 * Neden liste: "IP zorlaması kapalı" ile "kurtarma kodların bitmek üzere"
 * ayrı ekranlarda değil aynı şeritte görünmeli — kullanıcı panelin güvenlik
 * durumunu tek bakışta görsün.
 */
export const BACKSTAGE_WARNINGS = [
  /**
   * 🔴 Demo amaçlı 2FA atlama açık — ikinci faktör devre dışı.
   *
   * Listenin BAŞINDA duruyor: diğerleri "şunu da yapabilirsin" derken bu
   * "kapı şu an tek kilitle duruyor" diyor. Sıralama ekranda da korunuyor.
   */
  'TOTP_BYPASS_ACTIVE',
  'IP_ENFORCEMENT_OFF',
  'IP_RULES_EMPTY',
  'RECOVERY_CODES_LOW',
  'NGINX_OUT_OF_SYNC',
] as const;
export type BackstageWarning = (typeof BACKSTAGE_WARNINGS)[number];

// ──────────────────────────── TOTP kurulumu ────────────────────────────

/**
 * Kurulum verisi — HAYATTA BİR KEZ dönüyor.
 *
 * `secret` ve `otpauthUri` aynı sırrı iki biçimde taşıyor: QR okunamayan bir
 * cihazda (uzak masaüstü, ekran okuyucu) elle girilebilsin diye. `qrDataUri`
 * sunucuda üretiliyor — istemcide üretmek sırrı bir kez daha JavaScript
 * bağlamına sokardı.
 */
export interface BackstageTotpEnrollment {
  secret: string;
  otpauthUri: string;
  qrDataUri: string;
}

export const backstageTotpConfirmSchema = z.object({
  code: z
    .string()
    .min(6)
    .max(10)
    .transform((v) => v.replace(/\s/g, '')),
});
export type BackstageTotpConfirmInput = z.infer<typeof backstageTotpConfirmSchema>;

/** Kurtarma kodları — üretildiği anda bir kez. */
export interface BackstageRecoveryCodes {
  codes: string[];
  generatedAt: string;
}

// ──────────────────────────── IP kuralları ────────────────────────────

/**
 * CIDR doğrulaması burada BİLEREK gevşek (uzunluk + karakter kümesi).
 *
 * Asıl doğrulama sunucudaki `parseCidr` — adresin gerçekten ayrıştırılabilir
 * olmasını ve prefix uzunluğunun aileye uymasını orada denetliyoruz. Zod'a
 * tam bir IPv6 regex'i yazmak, bir gün gerçek ayrıştırıcıyla AYRIŞAN ikinci
 * bir doğruluk kaynağı üretirdi.
 */
export const backstageIpRuleSchema = z.object({
  cidr: z
    .string()
    .min(7)
    .max(49)
    .regex(/^[0-9a-fA-F.:/]+$/, 'CIDR yalnızca adres karakterleri içerebilir'),
  label: z.string().min(1).max(80),
  enabled: z.boolean().default(true),
});
export type BackstageIpRuleInput = z.infer<typeof backstageIpRuleSchema>;

export const backstageIpRuleUpdateSchema = backstageIpRuleSchema.partial();
export type BackstageIpRuleUpdateInput = z.infer<typeof backstageIpRuleUpdateSchema>;

export interface BackstageIpRuleRow {
  id: string;
  cidr: string;
  label: string;
  enabled: boolean;
  /** true → .env'den geliyor, panelden silinemez. */
  breakGlass: boolean;
  /** true → çağıranın şu anki IP'si bu kuralın içinde. */
  matchesCurrent: boolean;
  createdAt: string;
}

export interface BackstageIpOverview {
  rules: BackstageIpRuleRow[];
  enforcement: boolean;
  nginxSync: boolean;
  /** Çağıranın gördüğümüz IP'si — kural yazmadan önce ekranda gösteriliyor. */
  currentIp: string | null;
  /** nginx dosyası DB ile aynı mı. false → "uygula" düğmesi belirir. */
  nginxInSync: boolean;
}

export const backstageIpSettingsSchema = z.object({
  enforcement: z.boolean().optional(),
  nginxSync: z.boolean().optional(),
  sessionTtlMinutes: z.number().int().min(5).max(480).optional(),
  maxFailedAttempts: z.number().int().min(3).max(20).optional(),
  lockMinutes: z.number().int().min(1).max(1440).optional(),
});
export type BackstageIpSettingsInput = z.infer<typeof backstageIpSettingsSchema>;

// ─────────────────────── Yönetici ve oturum listesi ───────────────────────

export interface BackstageAdminRow {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  role: BackstageRole;
  status: BackstageAccessStatus;
  totpConfirmedAt: string | null;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  activeSessions: number;
  unusedRecoveryCodes: number;
  createdAt: string;
}

export const backstageGrantSchema = z.object({
  email: z.email().max(200),
  role: z.enum(BACKSTAGE_ROLES),
});
export type BackstageGrantInput = z.infer<typeof backstageGrantSchema>;

export const backstageAccessUpdateSchema = z.object({
  role: z.enum(BACKSTAGE_ROLES).optional(),
  status: z.enum(['ACTIVE', 'SUSPENDED']).optional(),
  /** true → TOTP sırrı silinir, kullanıcı yeniden kurmak zorunda kalır. */
  resetTotp: z.boolean().optional(),
});
export type BackstageAccessUpdateInput = z.infer<typeof backstageAccessUpdateSchema>;

export interface BackstageSessionRow {
  id: string;
  ip: string | null;
  userAgent: string | null;
  current: boolean;
  lastSeenAt: string;
  expiresAt: string;
  createdAt: string;
}

export interface BackstageEventRow {
  id: string;
  type: BackstageEventType;
  email: string | null;
  ip: string | null;
  userAgent: string | null;
  detail: Record<string, unknown> | null;
  createdAt: string;
}

export interface BackstagePage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
